import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { jwtDecode } from 'jwt-decode';
import Loader from '../components/Loader';
import ModernNavbarPureCSS from "../components/Navbar/ModernNavBarPureCSS";
import styles from './mapsearchV2.module.css';
// --- NEW IMPORTS ---
import { HardHat, Wrench, ShoppingCart, MapPin, Building2 } from 'lucide-react';
import BusinessCard from './BusinessCard';
import BusinessProfilePopup from './BusinessProfilePopup';
import LoginGateModal from '../components/Common/LoginGateModal'; 

// --- 1. PREDEFINED REGIONS (Copied from SearchForm) ---
const PREDEFINED_REGIONS = [
  { name: "Sydney Metro", postcode: "2000", radius: 40, type: "Region" },
  { name: "Western Sydney", postcode: "2150", radius: 30, type: "Region" },
  { name: "North Shore", postcode: "2060", radius: 20, type: "Region" },
  { name: "Newcastle & Hunter", postcode: "2300", radius: 50, type: "Region" },
  { name: "Wollongong", postcode: "2500", radius: 30, type: "Region" },
];

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

// ... [Keep MapComponent exactly as it was] ...
const MapComponent = ({ locations, center, activeMarkerIndex, onMarkerClick, onMapLoad }) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    });

    if (!isLoaded) return <div>Loading...</div>;

    const unselectedMarkerIcon = {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: "#ff0000",
        fillOpacity: 1,
        strokeColor: "white",
        strokeWeight: 3,
    };

    const selectedMarkerIcon = {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new window.google.maps.Size(60, 60),
        labelOrigin: new window.google.maps.Point(30, -15)
    };

    return (
        <GoogleMap
            onLoad={onMapLoad}
            center={center}
            zoom={13}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
            }}
        >
            {locations.map((location, index) => (
                <Marker
                    key={index}
                    position={{ lat: location.latitude, lng: location.longitude }}
                    onClick={() => onMarkerClick(index)}
                    icon={activeMarkerIndex === index ? selectedMarkerIcon : unselectedMarkerIcon}
                    zIndex={activeMarkerIndex === index ? 100 : 1}
                />
            ))}
        </GoogleMap>
    );
};

const MapSearchV2 = () => {

    const query = useQuery();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const locationWrapperRef = useRef(null); // Ref for closing dropdown

    const radiusFromQuery = query.get('radius'); 

    const onMapLoad = (map) => {
        mapRef.current = map;
    };

    const searchTermFromQuery = query.get('searchTerm');
    const postCodeFromQuery = query.get('postCode');
    const typeFromQuery = query.get('type');

    // --- STATE ---
    const [searchTerm, setSearchTerm] = useState(searchTermFromQuery || '');
    const [selectedType, setSelectedType] = useState(typeFromQuery || '');
    
    // --- NEW: Smart Location State ---
    const [locationInput, setLocationInput] = useState(''); // Text shown in input
    const [postCode, setPostCode] = useState(postCodeFromQuery || ''); // Actual value for search
    const [locationSuggestions, setLocationSuggestions] = useState([]); // Dropdown list

    const [results, setResults] = useState([]);
    const [professionResults, setProfessionResults] = useState([]);
    const [locations, setLocations] = useState([]);
    const [center, setCenter] = useState({ lat: -33.8688, lng: 151.2093 });
    const [activeMarkerIndex, setActiveMarkerIndex] = useState(null);
    const [selectedBusinessId, setSelectedBusinessId] = useState(null);
    const [selectedBusinessIdForProfile, setSelectedBusinessIdForProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [userType, setUserType] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [postCodeError, setPostCodeError] = useState('');

    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    // radius hook
    const [radius, setRadius] = useState(radiusFromQuery ? parseInt(radiusFromQuery) : 25);
    const [totalMatches, setTotalMatches] = useState(0); 


    // --- EFFECT: Initialize Location Input from URL ---
    useEffect(() => {
        if (postCodeFromQuery) {
            setPostCode(postCodeFromQuery);
            // If it's a known region, show the name, otherwise show the code
            const region = PREDEFINED_REGIONS.find(r => r.postcode === postCodeFromQuery);
            setLocationInput(region ? region.name : postCodeFromQuery);
        }
    }, [postCodeFromQuery]);

    useEffect(() => {
        const token = window.sessionStorage.getItem('token') || window.localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                setUsername(decodedToken.UserName);
                setUserType(decodedToken.UserType);
                setIsLoggedIn(true);
            } catch (e) {
                setIsLoggedIn(false);
            }
        } else {
            setIsLoggedIn(false);
        }
    }, []);

    // --- EFFECT: Close Dropdown on Click Outside ---
    useEffect(() => {
        function handleClickOutside(event) {
            if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
                setLocationSuggestions([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const performSearch = async () => {
            if (searchTermFromQuery && postCodeFromQuery) {
                setLoading(true);

                try {

                    // Ensure radius is an integer
                    const searchRadius = radiusFromQuery ? parseInt(radiusFromQuery) : 25;
                    setRadius(searchRadius); // Sync state if user pasted a URL

                    const response = await api.post(`/api/SearchLocations/searchFLEXbyLocation`, {
                        searchTerm: searchTermFromQuery,
                        postCode: postCodeFromQuery,
                        type: typeFromQuery,
                        within: searchRadius 
                    });

                    if (response.data.businesses && response.data.businesses.length > 0) {
                        setResults(response.data.businesses);
                        setLocations(response.data.locations);

                        setTotalMatches(response.data.totalMatchesBeforeLimit || response.data.TotalMatchesBeforeLimit || response.data.businesses.length);
                        
                        if (response.data.center) {
                            setCenter({
                                lat: response.data.center.latitude,
                                lng: response.data.center.longitude,
                            });
                        }
                    } else {
                        setResults([]);
                        setLocations([]);
                    }
                    // Do not clear selectedType here, it might be needed for re-search
                } catch (error) {
                    console.error('Error performing search:', error);
                }
                setLoading(false);
            }
        };
        performSearch();
    }, [searchTermFromQuery, postCodeFromQuery, typeFromQuery, radiusFromQuery]);

    // --- NEW: Handle Location Logic ---
    const handleLocationInputChange = async (e) => {
        const val = e.target.value;
        setLocationInput(val);
        setPostCodeError('');
        
        // If user clears input, clear underlying postcode
        if (val === '') setPostCode('');

        if (val.length < 2) {
            setLocationSuggestions([]);
            return;
        }

        // 1. Frontend Filter: Regions
        const regionMatches = PREDEFINED_REGIONS.filter(r => 
            r.name.toLowerCase().includes(val.toLowerCase())
        );

        // 2. Backend Search: Suburbs
        try {
            const res = await api.get(`/api/SearchLocations/suggest?query=${val}`);
            const apiSuggestions = res.data.map(item => ({
                name: item.displayName, 
                postcode: item.postcode,
                radius: 25, 
                type: "Suburb"
            }));
            setLocationSuggestions([...regionMatches, ...apiSuggestions]);
        } catch (error) {
            console.error("Error fetching locations", error);
            setLocationSuggestions(regionMatches);
        }
    };

    const selectLocation = (item) => {
        setLocationInput(item.name);
        setPostCode(item.postcode);
        
        // If the item has a specific radius (from PREDEFINED_REGIONS), use it
        if(item.radius) {
            setRadius(item.radius);
        } else {
            setRadius(25); // Default for suburbs
        }
        
        setLocationSuggestions([]);
        setPostCodeError('');
    };

    // --- Existing Profession Logic ---
    const handleSearchChange = async (e) => {
        const searchValue = e.target.value;
        setSearchTerm(searchValue);

        if (searchValue.length > 0) {
            try {
                const result = await api.get(`/api/SearchLocations/searchAll?search=${searchValue}`);
                setProfessionResults(result.data); 
            } catch (error) {
                console.error('Error fetching professions:', error);
            }
        } else {
            setProfessionResults([]);
        }
    };

    const addItem = (item) => {
        setSearchTerm(item.name);
        setSelectedType(item.type);
        setProfessionResults([]);
    };

    // --- MODIFIED: Search Submit ---
    const handleSearchSubmit = () => {
        let finalPostCode = postCode;

        // If user manually typed a 4-digit code without clicking dropdown
        if (!finalPostCode && /^\d{4}$/.test(locationInput)) {
            finalPostCode = locationInput;
            setPostCode(locationInput); // sync state
        }

        if (!finalPostCode) {
            setPostCodeError('Please select a valid suburb or enter a 4-digit postcode.');
            return;
        }

        // Navigate updates URL, which triggers the performSearch useEffect
        //navigate(`/map-search-v2?searchTerm=${searchTerm}&postCode=${finalPostCode}&type=${selectedType}`);

        navigate(`/map-search-v2?searchTerm=${searchTerm}&postCode=${finalPostCode}&type=${selectedType}&radius=${radius}`);
    
        
    };

    const handleMarkerClick = (index) => {
        if (!isLoggedIn) {
            setShowLoginModal(true); 
            return;
        }
        setActiveMarkerIndex(index);
        const businessId = results[index]?.businessId;
        setSelectedBusinessId(businessId || null);
        setSelectedBusinessIdForProfile(businessId || null);
    };

    const handleResultCardClick = (businessId) => {
        const index = results.findIndex(r => r.businessId === businessId);
        if (index !== -1) {
            setActiveMarkerIndex(index);
            setSelectedBusinessId(businessId);
            const location = locations[index];
            if(location && mapRef.current) {
                mapRef.current.panTo({ lat: location.latitude, lng: location.longitude });
            }
        }
        if (!isLoggedIn) {
            setShowLoginModal(true);
            return;
        }
        setSelectedBusinessIdForProfile(businessId);
    };

    const handleCloseProfile = () => {
        setSelectedBusinessIdForProfile(null);
    };

    return (
        <div className={styles.pageContainer}>
            <ModernNavbarPureCSS UserName={username} prefix={''} TabActiveId={'search'} />
            
            {showLoginModal && <LoginGateModal onClose={() => setShowLoginModal(false)} />}

            <main className={styles.mainContent}>
                {loading && <Loader />}
                
                <section className={styles.searchSection}>
                     <div className={styles.searchForm}>
                        
                        {/* --- MODIFIED: Smart Location Input --- */}
                        <div className={styles.searchInputContainer} ref={locationWrapperRef}>
                            <input
                                type="text"
                                value={locationInput}
                                onChange={handleLocationInputChange}
                                placeholder="Suburb or Postcode"
                                className={styles.searchInput}
                                autoComplete="off"
                            />
                            {postCodeError && <p className={styles.errorMessage}>{postCodeError}</p>}
                            
                            {/* Location Dropdown */}
                            {locationSuggestions.length > 0 && (
                                <ul className={styles.dropdownResults}>
                                    {locationSuggestions.map((item, index) => (
                                        <li key={index} className={styles.resultItem} onClick={() => selectLocation(item)}>
                                            {item.type === 'Region' ? (
                                                <MapPin className={styles.iconRegion} size={18} style={{marginRight: '8px', color: '#daa520'}} />
                                            ) : (
                                                <Building2 className={styles.iconSuburb} size={18} style={{marginRight: '8px', color: '#666'}} />
                                            )}
                                            <span>{item.name}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Profession Input (Kept mostly same, adjusted icon styling) */}
                        <div className={styles.searchInputContainer}>
                            <input
                                type="text"
                                placeholder="Electrician or install air cond.."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={styles.searchInput}
                            />
                            {professionResults.length > 0 && (
                                <ul className={styles.dropdownResults}>
                                    {Object.entries(
                                        professionResults.reduce((acc, item) => {
                                            if (!acc[item.type]) acc[item.type] = [];
                                            acc[item.type].push(item);
                                            return acc;
                                        }, {})
                                    ).map(([type, items]) => (
                                        <React.Fragment key={type}>
                                            <li className={styles.categoryHeader}>Select a {type}</li>
                                            {items.map((item) => (
                                                <li key={`${item.type}-${item.id}`} className={styles.resultItem} onClick={() => addItem(item)}>
                                                    {item.type === 'Profession' && <HardHat size={18} style={{marginRight: '8px', color: '#007bff'}} />}
                                                    {item.type === 'Service' && <Wrench size={18} style={{marginRight: '8px', color: '#007bff'}} />}
                                                    {item.type === 'Product' && <ShoppingCart size={18} style={{marginRight: '8px', color: '#007bff'}} />}
                                                    <span>{item.name}</span>
                                                </li>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </ul>
                            )}
                        </div>
                        
                        <button onClick={() => setShowFilters(!showFilters)} className={styles.filterButton}>
                            Filters
                        </button>

                        <div className={styles.radiusContainer}>
                            <select 
                                value={radius} 
                                onChange={(e) => setRadius(parseInt(e.target.value))} 
                                className={styles.radiusSelect}
                            >
                                <option value="5">5 km</option>
                                <option value="10">10 km</option>
                                <option value="25">25 km</option>
                                <option value="50">50 km</option>
                                <option value="100">100 km</option>
                            </select>
                        </div>

                        <button onClick={handleSearchSubmit} className={styles.searchButton}>
                            Search
                        </button>
                    </div>
                </section>

                <div className={styles.resultsAndMapContainer}>
                    {/* ... Rest of your results layout remains exactly the same ... */}
                    <div className={styles.resultsPanel} style={{ flex: selectedBusinessIdForProfile ? '0 0 25%' : '0 0 35%', minWidth: '320px' }}>
                        {/*
                        <h2 className={styles.resultsHeader}>
                            {results.length} results for "{searchTermFromQuery}" near {locationInput}
                        </h2>
                        */}

{/* Replace the old <h2> with this block */}
<div className={styles.resultsHeaderContainer}>
    
    {/* Row 1: The Main Title */}
    <h1 className={styles.searchTitle}>
        {searchTermFromQuery} <span className={styles.nearText}>near</span> {locationInput || postCodeFromQuery}
    </h1>

    {/* Row 2: Stats & Radius Badge */}
    <div className={styles.metaRow}>
        <span className={styles.resultCount}>
            <strong>{results.length}</strong> results shown
            {totalMatches > results.length && (
                <span className={styles.totalCount}> (found {totalMatches})</span>
            )}
        </span>
        
        <span className={styles.dotSeparator}>•</span>
        
        <div className={styles.radiusBadge}>
            Within {radius}km
        </div>
    </div>
</div>

                        {results.map((result) => (
                            <BusinessCard
                                key={result.businessId}
                                business={result}
                                isSelected={selectedBusinessId === result.businessId}
                                onCardClick={handleResultCardClick}
                                isGuest={!isLoggedIn} 
                            />
                        ))}
                    </div>
                    {selectedBusinessIdForProfile && (
                        <div className={styles.profilePanel} style={{ flex: '0 0 33%', minWidth: '320px' }}>
                            <BusinessProfilePopup
                                key={selectedBusinessIdForProfile}
                                businessId={selectedBusinessIdForProfile}
                                onClose={handleCloseProfile}
                            />
                        </div>
                    )}
                    <div className={styles.mapPanel} style={{ flex: '1', minWidth: '300px' }}>
                        <MapComponent
                            locations={locations}
                            center={center}
                            activeMarkerIndex={activeMarkerIndex}
                            onMarkerClick={handleMarkerClick}
                            onMapLoad={onMapLoad}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
};

export default MapSearchV2;